import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, formatApiError, startGoogleLogin } from "../components/AuthContext";

export default function Login() {
  const [email, setEmail] = useState("owner@northwood.example.com");
  const [password, setPassword] = useState("Demo1234!");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // 2FA second-step state
  const [needsTotp, setNeedsTotp] = useState(false);
  const [totpCode, setTotpCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/dashboard";

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const twoFA = needsTotp
        ? (useRecovery ? { recovery_code: totpCode } : { totp_code: totpCode })
        : {};
      await login(email, password, twoFA);
      navigate(from, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 403 && detail?.code === "totp_required") {
        setNeedsTotp(true);
        setError("");
      } else {
        setError(formatApiError(err));
      }
    }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between p-12 paper-noise border-r border-[var(--border)]"
           style={{ background: "var(--paper)" }}>
        <Link to="/" className="overline hover:text-[var(--ink)] transition" data-testid="login-link-home">
          ← Back to home
        </Link>
        <div>
          <div className="badge badge-outline">For Accounting Firms</div>
          <h1 className="font-heading text-5xl mt-6 leading-[1.05]" style={{ fontWeight: 500 }}>
            One place to run your <em className="italic" style={{ color: "var(--accent)" }}>entire practice</em>.
          </h1>
          <p className="text-[var(--ink-2)] mt-6 text-lg max-w-md">
            Clients, jobs, time and workflows — all stitched together. Spend less time chasing,
            more time advising.
          </p>
        </div>
        <div className="overline text-[10px]">© 2026 Emergent · Practice OS</div>
      </div>

      <div className="flex flex-col justify-center px-8 py-16 md:px-20">
        <div className="max-w-md w-full">
          <div className="overline mb-3">Sign in</div>
          <h2 className="font-heading text-3xl mb-8" style={{ fontWeight: 500 }}>Welcome back.</h2>

          <button
            type="button"
            onClick={startGoogleLogin}
            className="btn-secondary w-full justify-center mb-2"
            data-testid="login-google"
          >
            <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continue with Google
          </button>
          <div className="text-[11px] text-[var(--ink-3)] text-center mb-6" data-testid="login-google-hint">
            New here? Signing in with Google starts a free demo firm instantly. No password needed.
          </div>

          <div className="flex items-center gap-3 mb-6">
            <div className="flex-1 h-px bg-[var(--border)]" />
            <span className="overline">or with email</span>
            <div className="flex-1 h-px bg-[var(--border)]" />
          </div>

          <form onSubmit={onSubmit} data-testid="login-form" className="space-y-4">
            <Field id="email" label="Email" type="email" value={email} onChange={setEmail} testid="login-email" />
            <Field id="password" label="Password" type="password" value={password} onChange={setPassword} testid="login-password" />
            {needsTotp && (
              <div className="card p-4" style={{ background: "var(--subtle)" }} data-testid="login-totp-section">
                <div className="overline mb-1">{useRecovery ? "Recovery code" : "Two-factor code"}</div>
                <p className="text-[12px] text-[var(--ink-3)] mb-2">
                  {useRecovery
                    ? "Enter one of your saved recovery codes (XXXXX-XXXXX)."
                    : "Open your authenticator app and enter the 6-digit code."}
                </p>
                <input
                  autoFocus
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value)}
                  inputMode={useRecovery ? "text" : "numeric"}
                  autoComplete="one-time-code"
                  placeholder={useRecovery ? "XXXXX-XXXXX" : "123456"}
                  data-testid="login-totp-input"
                  className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[15px] font-mono tracking-wider"
                />
                <button type="button" onClick={() => { setUseRecovery((v) => !v); setTotpCode(""); }}
                  data-testid="login-totp-toggle-recovery"
                  className="text-[11px] text-[var(--accent)] hover:underline mt-2">
                  {useRecovery ? "Use authenticator code instead" : "Lost device? Use a recovery code"}
                </button>
              </div>
            )}
            {error && <div className="text-[13px] text-[var(--accent)]" data-testid="login-error">{error}</div>}
            <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="login-submit">
              {loading ? "Signing in…" : needsTotp ? "Verify code" : "Sign in"}
            </button>
          </form>

          <div className="text-[13px] text-[var(--ink-2)] mt-6">
            New here?{" "}
            <Link to="/register" className="text-[var(--accent)] hover:underline" data-testid="link-register">
              Create a firm
            </Link>
          </div>
          <div className="text-[12px] text-[var(--ink-3)] mt-3">
            Try demo: <code className="kbd">owner@northwood.example.com</code> / <code className="kbd">Demo1234!</code>
          </div>
        </div>
      </div>
    </div>
  );
}

export function Field({ id, label, type = "text", value, onChange, testid, autoComplete }) {
  return (
    <div>
      <label htmlFor={id} className="overline block mb-1.5">{label}</label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        data-testid={testid}
        className="w-full bg-[var(--paper)] border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px] text-[var(--ink)] focus:outline-none focus:border-[var(--ink)] transition"
      />
    </div>
  );
}
